import {
  Menu,
  X,
  LogOut,
  ChevronDown,
  LayoutDashboard,
  Wallet,
  TrendingUp,
  ArrowLeftRight,
  Coins,
  History,
  Gift,
  BadgeCheck,
  Settings,
  User,
  Shield,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useBodyScrollLock } from "@/lib/useBodyScrollLock";
import BrandLogo from "@/components/common/BrandLogo";
import PopularBadge from "@/components/common/PopularBadge";
import KycPill from "@/components/common/KycPill";

const PUBLIC_NAV = [
  { label: "Trade", href: "/trade/spot/BTC-USDT" },
  { label: "Market", href: "/markets" },
  { label: "OTC", href: "/otc", badge: "Popular", badgeOnTop: true },
  { label: "Download App", href: "/download" },
  { label: "About Us", href: "/about" },
  { label: "Blog", href: "/blog" },
  { label: "Support", href: "/support" },
];

const APP_NAV = [
  { label: "Dashboard", to: "/dashboard", icon: LayoutDashboard },
  { label: "Wallets", to: "/wallets", icon: Wallet },
  { label: "Markets", to: "/markets", icon: TrendingUp },
  { label: "Trade", to: "/trade/spot/BTC-USDT", icon: ArrowLeftRight },
  { label: "OTC", to: "/otc", icon: Coins },
  { label: "Activity", to: "/activity", icon: History },
  { label: "Rewards", to: "/rewards", icon: Gift },
  { label: "KYC", to: "/kyc", icon: BadgeCheck },
  { label: "Settings", to: "/settings", icon: Settings },
  { label: "Profile", to: "/profile", icon: User },
];

const PROFILE_MENU_NARROW_W = 280;
const PROFILE_MENU_WIDE_MIN_W = 1060;
const PROFILE_MENU_WIDE_BP = 1024;
const PROFILE_MENU_EDGE = 12;

function ProfileNavTile({ to, label, icon: Icon, onClick, testId, accent, compact }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className={`flex items-center ${compact ? "gap-1.5" : "gap-2"} shrink-0 rounded-[10px] transition hover:bg-cm-surface ${
        compact ? "px-1 py-2" : "px-2 py-2"
      } ${accent ? "border border-dashed border-cm-blue/30" : ""}`}
      data-testid={testId}
    >
      <span
        className={`${compact ? "w-6 h-6 rounded-[6px]" : "w-7 h-7 rounded-[7px]"} shrink-0 grid place-items-center border border-[var(--cm-border)] ${
          accent ? "bg-cm-blue/5 text-cm-blue" : "bg-cm-surface text-cm-blue"
        }`}
      >
        <Icon size={compact ? 13 : 14} strokeWidth={2} />
      </span>
      <span
        className={`font-semibold whitespace-nowrap leading-none ${
          compact ? "text-[10px]" : "text-[10.5px]"
        } ${accent ? "text-cm-blue" : "text-cm-navy"}`}
      >
        {label}
      </span>
    </Link>
  );
}

function ProfileMenu() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [wideMenu, setWideMenu] = useState(
    () => typeof window !== "undefined" && window.innerWidth >= PROFILE_MENU_WIDE_BP,
  );
  const [menuPos, setMenuPos] = useState({ top: 0, right: PROFILE_MENU_EDGE });
  const wrapRef = useRef(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  const itemCount = APP_NAV.length + (user?.role === "admin" ? 1 : 0);
  const compactTiles = wideMenu && itemCount >= 10;

  useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${PROFILE_MENU_WIDE_BP}px)`);
    const onChange = () => setWideMenu(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (!open || !btnRef.current) return undefined;

    const updatePos = () => {
      const btn = btnRef.current.getBoundingClientRect();
      setMenuPos({
        top: btn.bottom + 8,
        right: Math.max(PROFILE_MENU_EDGE, window.innerWidth - btn.right),
      });
    };

    updatePos();
    window.addEventListener("resize", updatePos);
    window.addEventListener("scroll", updatePos, true);
    return () => {
      window.removeEventListener("resize", updatePos);
      window.removeEventListener("scroll", updatePos, true);
    };
  }, [open, wideMenu]);

  useEffect(() => {
    function onDoc(e) {
      const t = e.target;
      if (
        wrapRef.current?.contains(t) ||
        menuRef.current?.contains(t)
      ) {
        return;
      }
      setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  if (!user || user === false) return null;

  const displayName =
    user.full_name ||
    (user.email ? user.email.split("@")[0] : "Account");
  const initial = (displayName || "?").charAt(0).toUpperCase();

  async function handleLogout() {
    await logout();
    setOpen(false);
    nav("/login");
  }

  const menuPanel = open ? (
    <div
      ref={menuRef}
      className="fixed bg-white rounded-[14px] shadow-[0_24px_48px_-16px_rgba(29,45,63,0.28)] border border-[var(--cm-border)] cm-fade-up flex flex-col"
      style={{
        top: menuPos.top,
        right: menuPos.right,
        left: "auto",
        width: wideMenu ? "fit-content" : PROFILE_MENU_NARROW_W,
        maxWidth: `calc(100vw - ${PROFILE_MENU_EDGE * 2}px)`,
        zIndex: 350,
      }}
      data-testid="profile-menu"
    >
      <div className="px-4 py-3 border-b border-[var(--cm-border)] grid grid-cols-[1fr_auto] items-center gap-4">
        <div className="min-w-0">
          <div className="text-[13px] font-bold text-cm-navy truncate">
            {displayName}
          </div>
          <div className="text-[11px] text-cm-grey truncate">{user.email}</div>
        </div>
        {wideMenu && (
          <button
            onClick={handleLogout}
            data-testid="profile-menu-logout"
            className="shrink-0 inline-flex items-center justify-center gap-2 px-3 py-2 text-[12px] font-semibold text-red-600 hover:bg-red-50 transition rounded-[10px] border border-[var(--cm-border)]"
          >
            <LogOut size={14} />
            Logout
          </button>
        )}
      </div>

      <div
        className={
          wideMenu
            ? "px-3 py-2.5 flex flex-nowrap items-center gap-1 overflow-x-hidden"
            : "py-1 max-h-[min(420px,55dvh)] overflow-y-auto overscroll-contain flex flex-col"
        }
        data-testid="profile-menu-items"
      >
        {APP_NAV.map((n) => (
          <ProfileNavTile
            key={n.to}
            to={n.to}
            label={n.label}
            icon={n.icon}
            onClick={() => setOpen(false)}
            testId={`profile-menu-${n.label.toLowerCase()}`}
            compact={compactTiles}
          />
        ))}
        {user.role === "admin" && (
          <ProfileNavTile
            to="/admin/kyc"
            label="Admin"
            icon={Shield}
            onClick={() => setOpen(false)}
            testId="profile-menu-admin"
            accent
            compact={compactTiles}
          />
        )}
      </div>

      {!wideMenu && (
        <button
          onClick={handleLogout}
          data-testid="profile-menu-logout"
          className="w-full flex items-center justify-center gap-2 px-4 py-3 text-[13px] font-semibold text-red-600 hover:bg-red-50 transition border-t border-[var(--cm-border)]"
        >
          <LogOut size={14} /> Logout
        </button>
      )}
    </div>
  ) : null;

  return (
    <div ref={wrapRef} className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="nav-profile"
        className="flex items-center gap-2 sm:gap-2.5 bg-white/90 backdrop-blur px-2.5 sm:px-3 py-2 rounded-[12px] shadow-[0_8px_20px_-8px_rgba(0,0,0,0.2)] hover:bg-white transition max-w-[calc(100vw-5rem)]"
      >
        <span className="w-8 h-8 rounded-full bg-cm-blue text-white grid place-items-center text-[13px] font-bold">
          {initial}
        </span>
        <span className="hidden sm:inline text-[14px] font-semibold text-cm-navy max-w-[120px] truncate">
          {displayName}
        </span>
        <ChevronDown
          size={14}
          className={`text-cm-grey transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {typeof document !== "undefined" && createPortal(menuPanel, document.body)}
    </div>
  );
}

export default function Header() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const loggedIn = user && user !== false;

  useBodyScrollLock(open);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const mobileMenu = open ? (
    <>
      <button
        type="button"
        className="lg:hidden fixed inset-0 z-[70] bg-cm-navy/45 backdrop-blur-[2px]"
        onClick={() => setOpen(false)}
        aria-label="Close menu"
        data-testid="nav-mobile-backdrop"
      />
      <div
        className="lg:hidden fixed inset-x-0 top-[72px] bottom-0 z-[80] bg-white border-t border-[var(--cm-border)] overflow-y-auto overscroll-contain shadow-[0_24px_48px_-24px_rgba(29,45,63,0.25)]"
        data-testid="nav-mobile-drawer"
      >
        <div className="cm-container py-4 pb-safe flex flex-col gap-1 min-h-full">
          {loggedIn && (
            <div className="flex items-center gap-2 px-2 pb-3 mb-1 border-b border-[var(--cm-border)]">
              <KycPill user={user} />
            </div>
          )}
          {PUBLIC_NAV.map((item) => (
            <Link
              key={item.label}
              to={item.href}
              onClick={() => setOpen(false)}
              className="py-3 px-2 text-[15px] font-medium text-cm-navy hover:bg-cm-surface rounded-lg inline-flex items-center gap-2 min-h-[44px]"
            >
              <span>{item.label}</span>
              {item.badgeOnTop && item.badge ? <PopularBadge /> : null}
            </Link>
          ))}
          {loggedIn ? (
            <>
              <div className="h-px bg-[var(--cm-border)] my-2" />
              <p className="px-2 text-[10px] font-bold uppercase tracking-wider text-cm-grey">Your account</p>
              {APP_NAV.map((n) => (
                <ProfileNavTile
                  key={n.to}
                  to={n.to}
                  label={n.label}
                  icon={n.icon}
                  onClick={() => setOpen(false)}
                  testId={`mobile-nav-${n.label.toLowerCase()}`}
                />
              ))}
              {user.role === "admin" && (
                <ProfileNavTile
                  to="/admin/kyc"
                  label="Admin"
                  icon={Shield}
                  onClick={() => setOpen(false)}
                  testId="mobile-nav-admin"
                  accent
                />
              )}
            </>
          ) : (
            <div className="flex gap-2 pt-3 mt-2">
              <Link
                to="/login"
                onClick={() => setOpen(false)}
                className="flex-1 h-[46px] grid place-items-center rounded-[12px] bg-cm-surface text-cm-blue font-semibold"
              >
                Log in
              </Link>
              <Link
                to="/register"
                onClick={() => setOpen(false)}
                className="flex-1 h-[46px] grid place-items-center rounded-[12px] bg-cm-blue text-white font-semibold"
              >
                Sign up
              </Link>
            </div>
          )}
        </div>
      </div>
    </>
  ) : null;

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-[60] w-full bg-white/95 backdrop-blur-md border-b border-[var(--cm-border)] pt-safe"
    >
      <div className="cm-container flex items-center h-[72px] lg:h-[80px] gap-4 lg:gap-10 min-w-0">
        <Link to="/" data-testid="nav-logo" className="flex items-center shrink-0">
          <BrandLogo size="md" testId="nav-logo-img" />
        </Link>

        <nav className="hidden lg:flex items-center gap-9 overflow-visible shrink-0">
          {PUBLIC_NAV.map((item) => (
            <Link
              key={item.label}
              to={item.href}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`inline-flex items-center gap-1.5 text-[14px] lg:text-[15px] font-semibold text-cm-navy/90 hover:text-cm-blue transition-colors whitespace-nowrap ${
                item.badgeOnTop ? "relative" : ""
              }`}
            >
              {item.badgeOnTop && item.badge ? (
                <PopularBadge className="absolute -top-[14px] left-1/2 -translate-x-1/2 pointer-events-none" testId="nav-otc-popular-badge" />
              ) : null}
              <span>{item.label}</span>
              {!item.badgeOnTop && item.badge && (
                <span
                  className="text-[7px] font-extrabold uppercase tracking-[0.1em] px-1.5 py-[3px] rounded-[4px] text-white leading-none shrink-0"
                  style={{
                    background:
                      "linear-gradient(135deg, #F4D27A 0%, #E0A933 100%)",
                  }}
                >
                  {item.badge}
                </span>
              )}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4 ml-auto shrink-0">
          {loggedIn ? (
            <>
              <KycPill user={user} />
              <ProfileMenu />
            </>
          ) : (
            <>
              <Link
                to="/login"
                data-testid="nav-login"
                className="text-[14px] lg:text-[15px] font-semibold text-cm-navy hover:text-cm-blue transition-colors"
              >
                Log in
              </Link>
              <Link
                to="/register"
                data-testid="nav-register"
                className="inline-flex items-center justify-center h-[44px] px-6 rounded-full bg-cm-blue text-white font-bold text-[14px] shadow-[0_10px_24px_-10px_rgba(2,44,237,0.45)] hover:bg-cm-blue-deep transition-colors"
              >
                Sign up
              </Link>
            </>
          )}
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden ml-auto shrink-0 w-11 h-11 grid place-items-center rounded-lg text-cm-navy hover:bg-black/5"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {typeof document !== "undefined" && createPortal(mobileMenu, document.body)}
    </header>
  );
}
